/**
 * Cache decorator utility for method-level caching
 * Provides automatic caching functionality for class methods
 */

import {CacheUtil} from "./cache";
import {CACHE_TIME_15S} from "./cachedContent";

/**
 * Cache key constants for different types of cached data
 */
export const CACHE_KEY = {
  /** Cache key prefix for vault data */
  VAULT: 'mirror_vault_',
  /** Cache key prefix for token schema data */
  TOKEN_SCHEMA: 'mirror_token_schema_',
};

/**
 * Decorator factory that adds caching functionality to class methods
 * 
 * @param cacheKey - Base cache key prefix for this method
 * @param objKeyField - Field name to extract from object argument (optional, if the cache key is derived from an object)
 * @param keyArgIndex - Index of the argument to use as cache key (default: 0)
 * @param forceRefreshArgIndex - Index of boolean argument to force cache refresh (default: -1, disabled)
 * 
 * @returns Decorator function that wraps the original method
 * 
 * @example
 * ```typescript
 * class VaultService {
 *   @Cacheable(CACHE_KEY.VAULT, 'id', 0, 1)
 *   async getVault(vaultId: string, forceRefresh?: boolean) {
 *     // Method implementation
 *   }
 * }
 * ```
 */
export function Cacheable(cacheKey: string, objKeyField: string = '', keyArgIndex: number = 0, forceRefreshArgIndex: number = -1) {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    // Store reference to original method
    const original = descriptor.value;
    
    // Replace method with cached version
    descriptor.value = async function (...args: any[]) {
      const cacheInstance = CacheUtil.getInstance();
      
      // Build cache key from arguments
      let key = cacheKey + args[keyArgIndex]

      // If objKeyField is specified, extract the field value from the object
      if (objKeyField !== '') {
        key = cacheKey + args[keyArgIndex][objKeyField];
      }

      // Check if we should skip cache (force refresh)
      if (forceRefreshArgIndex === -1 || !args[forceRefreshArgIndex]) {
        // Try to get cached result
        const cache = cacheInstance.getCache(key);
        if (cache) {
          return cache;
        }
      }

      // Execute original method if no cache hit
      const res = await original.apply(this, args);
      
      // Cache the result if it exists
      if (res) {
        cacheInstance.updateCache(key, res, CACHE_TIME_15S);
      }
      
      return res;
    };
  }
}